import { createContext, useContext, useMemo, type ReactNode } from 'react'
import type {
  Competition,
  ResultsState,
  Split,
  SplitsQuery,
  Standing,
  StandingsQuery,
} from '../types'

export interface ResultsProviderValue {
  competitions: Record<string, Competition[]>
  standings: (query: StandingsQuery) => ResultsState<Standing[]>
  splits: (query: SplitsQuery) => ResultsState<Split[]>
  defaultSourceKey?: string | null
}

const ResultsContext = createContext<ResultsProviderValue | null>(null)

function emptyState<T>(sourceKey: string, data: T): ResultsState<T> {
  return {
    sourceKey,
    status: 'unconfigured',
    lastFreshAt: null,
    errorCode: null,
    data,
  }
}

export function ResultsProvider({
  value,
  children,
}: {
  value?: ResultsProviderValue
  children: ReactNode
}) {
  const resolved = useMemo<ResultsProviderValue>(
    () =>
      value ?? {
        competitions: {},
        standings: (query) => emptyState(query.sourceKey ?? '', []),
        splits: (query) => emptyState(query.sourceKey ?? '', []),
        defaultSourceKey: null,
      },
    [value],
  )
  return <ResultsContext.Provider value={resolved}>{children}</ResultsContext.Provider>
}

function useResultsContext(): ResultsProviderValue {
  const context = useContext(ResultsContext)
  if (!context) throw new Error('Results hooks must be used inside <ResultsProvider>.')
  return context
}

export function useCompetitions(sourceKey?: string): Competition[] {
  const context = useResultsContext()
  return context.competitions[sourceKey ?? context.defaultSourceKey ?? ''] ?? []
}

export function useStandings(query: StandingsQuery): ResultsState<Standing[]> {
  const context = useResultsContext()
  return context.standings({
    ...query,
    sourceKey: query.sourceKey ?? context.defaultSourceKey ?? undefined,
  })
}

export function useSplits(query: SplitsQuery): ResultsState<Split[]> {
  const context = useResultsContext()
  return context.splits({
    ...query,
    sourceKey: query.sourceKey ?? context.defaultSourceKey ?? undefined,
  })
}
